// Login.js
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_API_URL || "https://api.hellastay.com")
    : "http://localhost:8000";

export default function Login({ setUser }) {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const canSubmit = useMemo(() => {
    const e = email.trim();
    return e.length > 3 && password.length >= 1 && !loading;
  }, [email, password, loading]);

  const login = async (e) => {
    e.preventDefault();
    if (loading) return;

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password; // не trim-ваме парола

    if (!cleanEmail || !cleanPassword) {
      setError(t("login.errors.enterEmailPassword"));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // нужно за cookie JWT
        body: JSON.stringify({ email: cleanEmail, password: cleanPassword }),
      });

      // ✅ Safe parse (ако backend върне не-JSON, да не crash-ва)
      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        data = { message: text };
      }

      if (!res.ok) {
        if (res.status === 401) {
          setError(data.message || t("login.errors.invalidEmailPassword"));
          return;
        }
        if (res.status === 400) {
          setError(data.message || t("login.errors.checkInput"));
          return;
        }
        if (res.status === 429) {
          setError(data.message || t("login.errors.tooManyAttempts"));
          return;
        }
        if (res.status >= 500) {
          setError(data.message || t("login.errors.serverError"));
          return;
        }
        setError(data.message || t("login.errors.loginFailed"));
        return;
      }

      if (!data.user) {
        setError(t("login.errors.missingUserPayload"));
        return;
      }

      setUser(data.user);
      navigate("/");
    } catch (err) {
      setError(t("login.errors.cannotConnect"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <h2>{t("login.title")}</h2>

        <div
          style={{
            color: "var(--muted)",
            fontWeight: 500,
            fontSize: 13,
            marginTop: -6,
          }}
        >
          {t("login.subtitle")}
        </div>

        {error && (
          <div style={{ color: "red", fontSize: 13, marginTop: 8 }}>
            {error}
          </div>
        )}

        <form onSubmit={login} style={{ marginTop: 10 }}>
          <input
            type="email"
            placeholder={t("login.emailPlaceholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            autoComplete="email"
          />

          <input
            type="password"
            placeholder={t("login.passwordPlaceholder")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError("");
            }}
            autoComplete="current-password"
          />

          <button
            type="submit"
            disabled={!canSubmit}
            style={{ opacity: canSubmit ? 1 : 0.6 }}
            title={canSubmit ? t("login.readyTitle") : t("login.enterEmailPasswordTitle")}
          >
            {loading ? t("login.loggingIn") : t("login.title")}
          </button>
        </form>

        <div className="switch">
          {t("login.noAccount")} <Link to="/register">{t("login.registerLink")}</Link>
        </div>
      </div>
    </div>
  );
}
